import { CurrencyPipe, DecimalPipe, LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { EstimateurBudgetDetailPrix } from '@components/estimateur-budget-detail-prix/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { LoaderComponent } from '@shared/components/loader';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { LogsService } from '@shared/services/logs';
import { PriceEstimatorService } from '@shared/services/price-estimator';
import { PriceEstimator } from '@shared/services/price-estimator/models';
import { BudgetEstimatorStore, CommonStore } from '@stores';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-estimateur-budget-detail-prix',
  imports: [
    TitleComponent,
    NgClass,
    SvgComponent,
    NgTemplateOutlet,
    LoaderComponent,
    TranslatePipe,
    CurrencyPipe,
    LowerCasePipe,
    ButtonComponent,
    DecimalPipe,
  ],
  templateUrl: './estimateur-budget-detail-prix.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstimateurBudgetDetailPrixComponent
  extends AbstractDynamicComponent<EstimateurBudgetDetailPrix>
  implements OnInit, OnDestroy
{
  readonly estimatedPrices = signal<PriceEstimator>(null!);
  readonly estimatedPricesLoaded = signal(false);
  readonly showPriceDetail = signal(true);
  private readonly router = inject(Router);
  private readonly budgetEstimatorStore = inject(BudgetEstimatorStore);
  private readonly priceEstimatorService = inject(PriceEstimatorService);
  private readonly budgetEstimatorUrl = inject(CommonStore).select('budgetEstimatorUrl');
  private readonly currentEstimate = this.budgetEstimatorStore.select('currentEstimate');
  private readonly recentEstimates = this.budgetEstimatorStore.select('recentEstimates');
  private readonly logs = inject(LogsService);

  ngOnInit(): void {
    this.budgetEstimatorStore.update('isBudgetEstimatorPage', true);
    this.initializeCurrentEstimate();
  }

  ngOnDestroy(): void {
    this.budgetEstimatorStore.update('isBudgetEstimatorPage', false);
  }

  private calculateCurrentEstimatePrice(estimateIds: number[]): void {
    this.priceEstimatorService
      .fetchEstimatedPrices(estimateIds)
      .pipe(
        take(1),
        tap((prices) => {
          this.estimatedPrices.set(prices);
          this.estimatedPricesLoaded.set(true);
          const currentEstimate = this.currentEstimate();
          const recentEstimates = this.recentEstimates();
          this.budgetEstimatorStore.update('currentEstimate', { ...currentEstimate, _priceEstimate: prices });
          if (currentEstimate?._id && recentEstimates) {
            const updatedRecentEstimates = recentEstimates.map((estimate) =>
              estimate._id === currentEstimate._id ? { ...estimate, _priceEstimate: prices } : estimate
            );

            this.budgetEstimatorStore.update('recentEstimates', updatedRecentEstimates);
          }
        })
      )
      .subscribe({
        error: (err) => this.logs.error('EstimateurBudgetDetailPrixComponent', 'fetchEstimatedPrices failed', err),
      });
  }

  private initializeCurrentEstimate(): void {
    const estimation = this.currentEstimate()!;
    if (estimation) {
      const {
        styleSelected,
        layoutSelected,
        sizeSelected,
        workPlanSelected,
        sinkTypeSelected,
        householdApplianceSelected,
        // householdApplianceSelectedNone,
        installationSelected,
        // recentEstimates,
      } = estimation;
      if (
        !styleSelected ||
        !layoutSelected ||
        !sizeSelected ||
        !workPlanSelected ||
        !sinkTypeSelected ||
        !installationSelected
      ) {
        this.logs.warn(
          'EstimateurBudgetDetailPrixComponent',
          'Incomplete estimation data, redirecting to budget estimator'
        );
        void this.router.navigate([this.budgetEstimatorUrl() ?? '']);
      } else {
        const styleId = styleSelected.id;
        const layoutId = layoutSelected.id;
        const sizeId = sizeSelected.id;
        const workPlanId = workPlanSelected.id;
        const sinkId = sinkTypeSelected.id;
        const householdApplianceIds = (householdApplianceSelected ?? []).map((appliance) => appliance.id);
        let installationId = installationSelected.id;
        if (installationId === 0) {
          installationId = estimation.installationSelected!.installationMatch?.find(
            (installation) => installation.implantationId === layoutId
          )?.id as number;
        }
        const estimateIds: number[] = [
          styleId,
          layoutId,
          sizeId,
          workPlanId,
          sinkId,
          ...householdApplianceIds,
          installationId,
        ];
        this.calculateCurrentEstimatePrice(estimateIds);
      }
    } else {
      this.logs.warn(
        'EstimateurBudgetDetailPrixComponent',
        'No estimation found in store, redirecting to budget estimator'
      );
      void this.router.navigate([this.budgetEstimatorUrl() ?? '']);
    }
  }
}
